'use client'
import * as React from 'react'
import { Typography } from '@repo/design-system-cf/components/typography'
import type { ContentLinkBlock } from 'codegen/types'
import { Link } from '@repo/web-ui/components/link'
import { useTranslations } from 'next-intl'
import { Button } from '@repo/design-system-cf/components/button'
import { IconChevronRight } from '@repo/design-system-cf/components/icons'
import { tw } from 'utils/classnames'
import { InlineStaticVariant } from '@repo/web-ui/components/static-variants-renderer/inline/inline-static-variant'
import { LOGGED_OUT } from '@repo/web-ui/components/static-variants-renderer/utils/conditions'

export function LinksBlock({ linkBlocks }: { linkBlocks: ContentLinkBlock[] }) {
  return (
    <>
      <InlineStaticVariant
        conditions={[LOGGED_OUT]}
        getVariant={({ conditionsMet }) =>
          conditionsMet ? <LinksBlockClientComponent linkBlocks={linkBlocks} /> : null
        }
      />
    </>
  )
}

export function LinksBlockClientComponent({ linkBlocks }: { linkBlocks: ContentLinkBlock[] }) {
  const t = useTranslations('pages.fonts.links')

  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-col gap-3 text-center">
        <Typography variant="cf-title-05" asChild>
          <h2>{t('title')}</h2>
        </Typography>
        <Typography variant="cf-label-02" asChild>
          <p>{t('subtitle')}</p>
        </Typography>
      </div>
      <div className="flex flex-col">
        {linkBlocks.map(linkBlock => (
          <LinkBlock key={linkBlock.id} linkBlock={linkBlock} />
        ))}
      </div>
    </div>
  )
}

function LinkBlock({ linkBlock }: { linkBlock: ContentLinkBlock }) {
  const [collapsed, setCollapsed] = React.useState(true)
  return (
    <div className={tw('flex flex-col border-b border-cf-grey-200')}>
      <Button asChild variant="ghost" key={linkBlock.id} onClick={() => setCollapsed(!collapsed)}>
        <button className="flex gap-2 items-center py-4">
          <Typography
            variant="cf-title-04"
            asChild
            className="grow text-center translate-x-4 truncate"
          >
            <p>{linkBlock.title}</p>
          </Typography>
          <IconChevronRight
            className={tw('ml-auto w-8 transition-transform', {
              '-rotate-90': collapsed,
              'rotate-90': !collapsed,
            })}
          />
        </button>
      </Button>
      <div
        className={tw(
          'grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 transition-transform',
          {
            'h-0 overflow-hidden': collapsed,
            'h-auto py-4': !collapsed,
          },
        )}
      >
        {linkBlock.links.map(link => (
          <Link
            key={link.anchor}
            href={link.url}
            className="truncate text-[#0a5ea8] hover:text-[#3ab561] transition-colors"
          >
            {link.anchor}
          </Link>
        ))}
      </div>
    </div>
  )
}
